'use client'

import { QueryClient, QueryClientProvider, focusManager, onlineManager, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { GlobalState, GlobalEvents } from '@highlight/ipc'
import { STAGED_ACTIONS_JOBS_QUERY_KEY } from '@renderer/overlay/hooks/use-staged-action-jobs'
import { registerChatMetadataSync } from '@renderer/stores/chat-history-cache'
import { CONNECTIONS_QUERY_KEY } from '@renderer/hooks/integration-query-keys'
import { GatewayEvents } from '@shared/ipc/GatewayEvents'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - queries won't refetch on window focus until stale
      gcTime: 60 * 60 * 1000, // 1 hour - keep cached data around so reopening sidebar/chat is instant
      refetchOnWindowFocus: true,
      refetchOnReconnect: 'always',
    },
  },
})

export function ReactQueryProvider(props: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryStateSync />
      {props.children}

      {/* -- Uncomment the line below for react query dev tools -- */}
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  )
}

/**
 * Sometimes the browser window will not emit online events when resuming from sleep and reconnecting
 * if the system went to sleep while connected to wi-fi. To handle these, we must override the `focusManager`
 * and `onlineManager` event listeners, and listen to our own global state/events to cover all bases.
 */
export function ReactQueryStateSync() {
  const queryClient = useQueryClient()

  // Peer windows' optimistic chat metadata writes (rename, share, delete).
  // Registered here rather than in an overlay provider because generic windows
  // (artifact previews, side-panel chat) hold the same caches and publish
  // patches too; every cache-holding window must listen.
  useEffect(() => registerChatMetadataSync(queryClient), [queryClient])

  // Registered at the provider so invalidation fires even when no useAudioNotes
  // consumer is mounted when the meeting ends — otherwise the cache stays stale
  // until staleTime elapses and the new note is missing from History.
  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['audio-notes'] })
      // Also refetch the by-id note backing an open Meeting View or note window,
      // so a meeting opened from the upcoming section populates its transcript
      // (and summary) in place when saving finishes — no back-out-and-reopen.
      queryClient.invalidateQueries({ queryKey: ['audio-note'] })
      // The saved refetch can race the webhook's job insert; summary-ready reliably picks it up.
      queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_JOBS_QUERY_KEY] })
    }
    GlobalEvents.on('audio-note:saved', invalidate)
    GlobalEvents.on('audio-note:summary-ready', invalidate)
    return () => {
      GlobalEvents.off('audio-note:saved', invalidate)
      GlobalEvents.off('audio-note:summary-ready', invalidate)
    }
  }, [queryClient])

  // Same reasoning for skills: the sidepanel's slash / @mention menu mounts
  // `useSkills` only while the popover is open, so a delete or add fired from
  // Settings would land on a sidepanel with no listener. Park it here so the
  // skills cache invalidates whenever any window adds or removes a skill.
  // (Event name must match SKILLS_CHANGED_EVENT in chat/hooks/useSkills.ts.)
  useEffect(() => {
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] })
      queryClient.invalidateQueries({ queryKey: ['default-skills'] })
    }
    GlobalEvents.on('skills:changed', invalidate)
    return () => {
      GlobalEvents.off('skills:changed', invalidate)
    }
  }, [queryClient])

  // Same reasoning for connections: a window with no `useConnections` consumer
  // mounted at connect time would otherwise miss the event and show the old list.
  useEffect(
    () =>
      GatewayEvents.onChannel('connection-updated', () => {
        queryClient.invalidateQueries({ queryKey: [CONNECTIONS_QUERY_KEY] })
        queryClient.invalidateQueries({ queryKey: ['calendars'] })
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      }),
    [queryClient],
  )

  useEffect(() => {
    focusManager.setEventListener((handleFocus) => {
      if (typeof window === 'undefined') {
        return
      }

      const onFocus = () => {
        // console.log(
        //   `ReactQueryWindowFocusSync: onFocus, onlineManager=${onlineManager.isOnline() ? 'online' : 'offline'}, navigator=${navigator.onLine ? 'online' : 'offline'}`,
        // )
        handleFocus(true)
      }

      const onBlur = () => {
        // console.log(
        //   `ReactQueryWindowFocusSync: onBlur, onlineManager=${onlineManager.isOnline() ? 'online' : 'offline'}, navigator=${navigator.onLine ? 'online' : 'offline'}`,
        // )
        handleFocus(false)
      }

      const onWindowFocused = (focused: boolean) => {
        // console.log(
        //   `ReactQueryWindowFocusSync: onWindowFocused(${focused}), onlineManager=${onlineManager.isOnline() ? 'online' : 'offline'}, navigator=${navigator.onLine ? 'online' : 'offline'}`,
        // )
        handleFocus(focused)
      }

      window.addEventListener('focus', onFocus)
      window.addEventListener('blur', onBlur)
      GlobalState.on('window.focused', onWindowFocused)

      return () => {
        window.removeEventListener('focus', onFocus)
        window.removeEventListener('blur', onBlur)
        GlobalState.off('window.focused', onWindowFocused)
      }
    })
  }, [])

  useEffect(() => {
    onlineManager.setEventListener((setOnline) => {
      const onOnline = () => {
        console.log(`ReactQueryWindowFocusSync: onOnline`)
        setOnline(true)
        console.log(
          `ReactQueryWindowFocusSync: onOnline new onlineManager state online=${onlineManager.isOnline()}, navigator=${navigator.onLine ? 'online' : 'offline'}`,
        )
      }

      const onOffline = () => {
        console.log(`ReactQueryWindowFocusSync: onOffline`)
        setOnline(false)
        console.log(
          `ReactQueryWindowFocusSync: onOffline new onlineManager state online=${onlineManager.isOnline()}, navigator=${navigator.onLine ? 'online' : 'offline'}`,
        )
      }

      const onNetworkStatus = (status: 'pending' | 'online' | 'offline') => {
        console.log(`ReactQueryWindowFocusSync: onNetworkStatus status=${status}`)
        if (status === 'online' && !onlineManager.isOnline()) {
          console.log(`ReactQueryWindowFocusSync: onNetworkStatus forced back online`)
          setOnline(true)
        }
      }

      window.addEventListener('online', onOnline)
      window.addEventListener('offline', onOffline)
      GlobalState.on('networkStatus', onNetworkStatus)

      return () => {
        window.removeEventListener('online', onOnline)
        window.removeEventListener('offline', onOffline)
        GlobalState.off('networkStatus', onNetworkStatus)
      }
    })
  }, [])

  return null
}
